import { useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import { Notice } from "./ui";

/** Public review uses the same published snapshot as a script embed. */
export function PublicPreviewLink({
  mapId,
  published,
}: {
  mapId: string;
  published: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const link = `${window.location.origin}/preview.html?map=${encodeURIComponent(mapId)}`;

  async function copyLink() {
    if (!published) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setError("");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError(
        "Copy is unavailable in this browser. Select the link above and copy it manually.",
      );
    }
  }

  return (
    <section
      className="em-public-preview"
      aria-labelledby="public-preview-heading"
    >
      <div className="em-panel-title">
        <div>
          <h2 id="public-preview-heading">Public preview</h2>
          <p>Share an interactive map with stakeholders. No sign-in needed.</p>
        </div>
      </div>
      <label>
        Public preview link
        <input
          className="em-code-input"
          readOnly
          disabled={!published}
          value={link}
          aria-describedby="public-preview-help"
          onFocus={(event) => event.currentTarget.select()}
        />
      </label>
      <p id="public-preview-help" className="em-field-help">
        {published
          ? "Anyone with the link can view the latest published map. Draft changes stay private until you publish."
          : "Publish your map to enable this link. Drafts stay private."}
      </p>
      <div className="em-actions em-actions-wrap">
        <button
          type="button"
          className="em-button em-button-secondary"
          disabled={!published}
          onClick={() => void copyLink()}
        >
          {copied ? (
            <Check size={16} aria-hidden="true" />
          ) : (
            <Copy size={16} aria-hidden="true" />
          )}
          {copied ? "Link copied" : "Copy link"}
        </button>
        {published ? (
          <a
            className="em-button em-button-secondary"
            href={link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={16} aria-hidden="true" />
            Open preview
          </a>
        ) : (
          <button
            type="button"
            className="em-button em-button-secondary"
            disabled
          >
            <ExternalLink size={16} aria-hidden="true" />
            Open preview
          </button>
        )}
      </div>
      <span className="em-sr-only" role="status">
        {copied ? "Preview link copied." : ""}
      </span>
      {error && <Notice>{error}</Notice>}
    </section>
  );
}
